import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

export default function DownloadPage() {
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Download the Latest Kakobuy Spreadsheet Collection - Free Access",
    "description": "Access the complete Kakobuy Spreadsheet database with 10,000+ updated product finds, organized categories, easy comparison tools, and beginner-friendly shopping guides. Free download and access available.",
    "url": "https://kakobuyfinds.sbs/kakobuy-spreadsheet-download",
    "about": {
      "@type": "Thing",
      "name": "Kakobuy Spreadsheet",
      "description": "Complete product database with updated finds, organized by 14 categories."
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How can I download the Kakobuy Spreadsheet?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "You can access the complete Kakobuy Spreadsheet by visiting kaspreadsheet.com. The spreadsheet is browser-accessible and does not require any software download — simply visit the website and start browsing through 14 organized categories with 10,000+ products. It is completely free to use."
        }
      },
      {
        "@type": "Question",
        "name": "Is the Kakobuy Spreadsheet free to download and use?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, the Kakobuy Spreadsheet is completely free. There are no download fees, subscription costs, or hidden charges. The platform is community-supported and provides free access to all 14 categories and 10,000+ product listings for every shopper."
        }
      },
      {
        "@type": "Question",
        "name": "What is included in the Kakobuy Spreadsheet collection?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "The Kakobuy Spreadsheet collection includes 10,000+ curated product finds organized across 14 categories: Shoes, Hoodies & Sweaters, T-Shirts, Jackets, Pants & Shorts, Headwear, Sets, Underwear, Jersey, Accessories, Bags, Belts, Perfumes, and Other Stuff. Each category is updated weekly with new trending products and community recommendations."
        }
      },
      {
        "@type": "Question",
        "name": "Do I need special software to use the Kakobuy Spreadsheet?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "No special software is required. The Kakobuy Spreadsheet is accessible through any modern web browser on desktop, tablet, or mobile devices. Simply visit kaspreadsheet.com and you can start browsing products immediately without any downloads or installations."
        }
      }
    ]
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

      <Navbar />
      <main className="pt-20">
        <section className="relative w-full min-h-[400px] md:min-h-[450px] flex items-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Clean%20modern%20digital%20workspace%20with%20laptop%20displaying%20colorful%20organized%20product%20spreadsheet%20on%20screen%2C%20floating%20digital%20icons%20representing%20file%20download%20and%20cloud%20access%2C%20bright%20airy%20atmosphere%2C%20soft%20white%20and%20light%20gray%20tones%2C%20professional%20tech%20aesthetic%2C%20inspiring%20workspace%20photography&width=1800&height=700&seq=download-hero-08&orientation=landscape"
              alt="Kakobuy Spreadsheet download and access"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/15 to-black/35"></div>
          </div>
          <div className="relative z-10 w-full px-4 md:px-6 text-center">
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-white mb-4">
              Download the Latest Kakobuy Sheet Collection
            </h1>
            <p className="text-base md:text-lg text-white/80 max-w-2xl mx-auto mb-8">
              Access 10,000+ updated product finds across 14 organized categories. No software required — start browsing instantly.
            </p>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-primary-500 text-white text-sm font-semibold rounded-full hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer shadow-lg shadow-primary-500/25"
            >
              Access Spreadsheet Database
              <i className="ri-arrow-right-up-line"></i>
            </a>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-background-50">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-14">
              <div className="bg-background-100 rounded-2xl p-6 md:p-8 border border-background-200/60">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 mb-4">
                  <i className="ri-database-2-line text-2xl"></i>
                </div>
                <h3 className="font-heading text-xl font-bold text-foreground-900 mb-3">Updated Product Database</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  Access over 10,000 curated products refreshed weekly with new trending finds, community recommendations, and verified listings across all 14 categories.
                </p>
              </div>
              <div className="bg-background-100 rounded-2xl p-6 md:p-8 border border-background-200/60">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 mb-4">
                  <i className="ri-folder-2-line text-2xl"></i>
                </div>
                <h3 className="font-heading text-xl font-bold text-foreground-900 mb-3">14 Organized Categories</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  Products are logically sorted into Shoes, Hoodies, T-Shirts, Jackets, Pants, Headwear, Sets, Underwear, Jersey, Accessories, Bags, Belts, Perfumes, and Other Stuff.
                </p>
              </div>
              <div className="bg-background-100 rounded-2xl p-6 md:p-8 border border-background-200/60">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 mb-4">
                  <i className="ri-scales-line text-2xl"></i>
                </div>
                <h3 className="font-heading text-xl font-bold text-foreground-900 mb-3">Easy Product Comparison</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  The spreadsheet format makes side-by-side product comparison natural and intuitive. Compare prices, features, and community feedback at a glance.
                </p>
              </div>
              <div className="bg-background-100 rounded-2xl p-6 md:p-8 border border-background-200/60">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 mb-4">
                  <i className="ri-user-smile-line text-2xl"></i>
                </div>
                <h3 className="font-heading text-xl font-bold text-foreground-900 mb-3">100% Beginner Friendly</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  No technical skills required. The clean interface and logical category system mean you can start discovering products within seconds of your first visit.
                </p>
              </div>
            </div>

            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                How to Access the Kakobuy Spreadsheet
              </h2>
              <p className="text-sm md:text-base text-foreground-500 max-w-xl mx-auto">
                Getting started takes less than a minute. No downloads, no installations, no sign-ups required.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-14">
              <div className="text-center p-6">
                <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-600 text-xl font-bold mb-4">1</div>
                <h4 className="font-heading text-lg font-semibold text-foreground-900 mb-2">Visit the Main Site</h4>
                <p className="text-sm text-foreground-500">Navigate to kaspreadsheet.com from any device with a modern web browser.</p>
              </div>
              <div className="text-center p-6">
                <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-600 text-xl font-bold mb-4">2</div>
                <h4 className="font-heading text-lg font-semibold text-foreground-900 mb-2">Pick a Category</h4>
                <p className="text-sm text-foreground-500">Choose from 14 categories — Shoes, Hoodies, Accessories, and more — to start browsing.</p>
              </div>
              <div className="text-center p-6">
                <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-600 text-xl font-bold mb-4">3</div>
                <h4 className="font-heading text-lg font-semibold text-foreground-900 mb-2">Start Discovering</h4>
                <p className="text-sm text-foreground-500">Browse curated product listings, compare options, and find exactly what you are looking for.</p>
              </div>
            </div>

            <div className="text-center">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-8 py-3.5 bg-foreground-900 text-white text-sm font-semibold rounded-full hover:bg-foreground-800 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Access Spreadsheet Now
                <i className="ri-arrow-right-up-line"></i>
              </a>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="prose-custom max-w-none">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-6 text-center">
                Why Thousands of Shoppers Trust the Kakobuy Spreadsheet
              </h2>
              <p className="text-foreground-600 leading-relaxed mb-4">
                The <strong>Kakobuy Spreadsheet</strong> has earned its reputation as the most trusted resource in the spreadsheet shopping community through consistent quality, transparent curation, and genuine community engagement. Unlike platforms that prioritize algorithmic recommendations, the Kakobuy approach puts real human curation at the center of the experience.
              </p>
              <p className="text-foreground-600 leading-relaxed mb-4">
                Every product listing in the spreadsheet has been reviewed and organized by actual shoppers who understand what makes a product worth recommending. This human touch results in a database that feels curated rather than computer-generated — products are placed in categories where they actually belong, described in terms that real shoppers use, and updated based on genuine community feedback rather than engagement metrics.
              </p>
              <p className="text-foreground-600 leading-relaxed mb-4">
                The weekly update schedule means the spreadsheet never feels stale. Each week brings fresh finds, removes outdated listings, and incorporates community suggestions. For shoppers who browse regularly, this consistent refresh creates a reason to return and discover something new each visit.
              </p>
              <p className="text-foreground-600 leading-relaxed">
                Ready to join thousands of satisfied shoppers? <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-accent-600 hover:text-accent-700 underline font-medium cursor-pointer">Access the Kakobuy Sheet now</a> and experience the difference that real curation makes. Explore our <Link to="/kakobuy-spreadsheet-guide" className="text-accent-600 hover:text-accent-700 underline font-medium cursor-pointer">complete spreadsheet guide</Link>, <Link to="/kakobuy-spreadsheet-finds" className="text-accent-600 hover:text-accent-700 underline font-medium cursor-pointer">best finds of 2026</Link>, and <Link to="/spreadsheet-comparison" className="text-accent-600 hover:text-accent-700 underline font-medium cursor-pointer">platform comparison hub</Link> for even more resources.
              </p>
            </div>

            <div className="mt-12 p-6 bg-background-50 rounded-2xl border border-background-200/60">
              <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-4">Related Resources</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Link to="/" className="flex items-center gap-2 p-3 bg-background-100 rounded-xl text-sm text-foreground-700 hover:text-foreground-900 hover:bg-background-200/40 transition-colors cursor-pointer">
                  <i className="ri-home-4-line text-accent-500"></i> Back to Homepage
                </Link>
                <Link to="/kakobuy-spreadsheet-guide" className="flex items-center gap-2 p-3 bg-background-100 rounded-xl text-sm text-foreground-700 hover:text-foreground-900 hover:bg-background-200/40 transition-colors cursor-pointer">
                  <i className="ri-guide-line text-accent-500"></i> Complete Spreadsheet Guide
                </Link>
                <Link to="/kakobuy-spreadsheet-finds" className="flex items-center gap-2 p-3 bg-background-100 rounded-xl text-sm text-foreground-700 hover:text-foreground-900 hover:bg-background-200/40 transition-colors cursor-pointer">
                  <i className="ri-star-line text-accent-500"></i> Best Kakobuy Finds 2026
                </Link>
                <Link to="/spreadsheet-comparison" className="flex items-center gap-2 p-3 bg-background-100 rounded-xl text-sm text-foreground-700 hover:text-foreground-900 hover:bg-background-200/40 transition-colors cursor-pointer">
                  <i className="ri-scales-line text-accent-500"></i> Platform Comparison Hub
                </Link>
                <Link to="/kakobuy-spreadsheet-beginners" className="flex items-center gap-2 p-3 bg-background-100 rounded-xl text-sm text-foreground-700 hover:text-foreground-900 hover:bg-background-200/40 transition-colors cursor-pointer">
                  <i className="ri-book-open-line text-accent-500"></i> Beginners Tutorial
                </Link>
                <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="flex items-center gap-2 p-3 bg-background-100 rounded-xl text-sm text-foreground-700 hover:text-foreground-900 hover:bg-background-200/40 transition-colors cursor-pointer">
                  <i className="ri-external-link-line text-accent-500"></i> Visit Kakobuy Sheet Now
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}